import { Action, MiddlewareAPI, Dispatch } from '@reduxjs/toolkit';
import { RootState } from '../store/store';
import { updateStudySession } from '../services/studySession';
import { selectTodaySession } from '../slices/todaySession/todaySessionSlice';

// Handler function type
type ActionHandler = (
  store: MiddlewareAPI,
  action: Action,
  prevState: RootState,
  newState: RootState
) => void;

// Sync completed flashcard count with study session
const syncCompletedFlashcardCount: ActionHandler = (
  _store,
  _action,
  prevState,
  newState
) => {
  const prevCompletedCount = prevState.flashcards?.completed?.length ?? 0;
  const newCompletedCount = newState.flashcards?.completed?.length ?? 0;
  const todaySession = selectTodaySession(newState);

  // Only update if the count actually changed and we have a valid session
  if (
    newCompletedCount !== prevCompletedCount &&
    todaySession.id &&
    todaySession.completedFlashcardCount !== newCompletedCount
  ) {
    updateStudySession({
      id: todaySession.id,
      completedFlashcardCount: newCompletedCount,
    }).catch((error) => {
      console.error('Failed to sync completed flashcard count:', error);
    });
  }
};

// Configuration: map action types to their handlers
const ACTION_HANDLERS: Record<string, ActionHandler[]> = {
  'flashcards/flashcardsLoaded': [syncCompletedFlashcardCount],
  'flashcards/flashcardUpdated': [syncCompletedFlashcardCount],
  'flashcards/flashcardSuspended': [syncCompletedFlashcardCount],
  'flashcards/flashcardRemoved': [syncCompletedFlashcardCount],
};

export const actionHandlersMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: Action) => {
    // Get state before the action
    const prevState = store.getState();

    // Process the action
    const result = next(action);

    // Check if we have handlers for this action type
    const handlers = ACTION_HANDLERS[action.type];
    if (handlers) {
      const newState = store.getState();

      // Run all handlers for this action type
      handlers.forEach((handler) => {
        try {
          handler(store, action, prevState, newState);
        } catch (error) {
          console.error(`Error in handler for ${action.type}:`, error);
        }
      });
    }

    return result;
  };
